import { createReducer, on } from '@ngrx/store';
import { IAsyncState, IBfFeed } from '@studio/domain';
import { IElement } from '@studio/domain/creativeset';
import { VersionToDirtyProperties } from '@studio/domain/translation-page';
import { omit } from '@studio/utils/utils';
import * as TranslationPageActions from './translation-page.actions';
import { findElementIndex } from './translation-page.utils';

export const TRANSLATION_PAGE_FEATURE_KEY = 'translationPage';

export interface TranslationPagePartialState {
    readonly [TRANSLATION_PAGE_FEATURE_KEY]: TranslationPageState;
}

export interface TranslationPageState extends IAsyncState {
    initialized: boolean;
    elements: IElement[];
    selectedElementId: string | undefined;
    isShowingAllElements: boolean;

    dirtyProperties: VersionToDirtyProperties;

    feedSources: IBfFeed[];
}

export const initialState: TranslationPageState = {
    loaded: false,
    initialized: false,

    elements: [],
    selectedElementId: undefined,
    isShowingAllElements: false,

    dirtyProperties: {},

    feedSources: []
};

export const reducer = createReducer(
    initialState,

    on(
        TranslationPageActions.loadTranslationPage,
        TranslationPageActions.saveDirtyVersions,
        (state): TranslationPageState => ({ ...state, loaded: false, error: undefined })
    ),
    on(TranslationPageActions.loadTranslationPageSuccess, (state): TranslationPageState => {
        return {
            ...state,
            initialized: true
        };
    }),
    on(
        TranslationPageActions.loadTranslationPageSuccess,
        TranslationPageActions.updateElementsOnSizeChangeSuccess,
        (state, { elements }): TranslationPageState => {
            return {
                ...state,
                loaded: true,
                elements,
                selectedElementId: elements[0]?.id,
                isShowingAllElements: false,
                dirtyProperties: {}
            };
        }
    ),
    on(
        TranslationPageActions.loadTranslationPageFailure,
        TranslationPageActions.saveDirtyVersionsFailure,
        (state, { error }): TranslationPageState => {
            return { ...state, loaded: true, error };
        }
    ),
    on(TranslationPageActions.loadTranslationPageFailure, (state, { error }): TranslationPageState => {
        return { ...state, loaded: true, error };
    }),

    on(TranslationPageActions.selectElement, (state, { elementId }): TranslationPageState => {
        return {
            ...state,
            selectedElementId: elementId
        };
    }),

    on(TranslationPageActions.selectNextElement, (state): TranslationPageState => {
        const newIndex = findElementIndex(state.elements, state.selectedElementId);

        return {
            ...state,
            selectedElementId: state.elements[Math.min(newIndex + 1, state.elements.length - 1)]?.id
        };
    }),
    on(TranslationPageActions.selectPreviousElement, (state): TranslationPageState => {
        const newIndex = findElementIndex(state.elements, state.selectedElementId);

        return {
            ...state,
            selectedElementId: state.elements[Math.max(0, newIndex - 1)]?.id
        };
    }),

    on(
        TranslationPageActions.upsertDirtyVersionProperty,
        (state, { versionId, versionProperty }): TranslationPageState => {
            return {
                ...state,
                dirtyProperties: {
                    ...state.dirtyProperties,
                    [versionId]: {
                        ...state.dirtyProperties[versionId],
                        [versionProperty.id]: versionProperty
                    }
                }
            };
        }
    ),

    on(
        TranslationPageActions.removeDirtyVersionProperty,
        (state, { versionId, versionPropertyId }): TranslationPageState => {
            return {
                ...state,
                dirtyProperties: {
                    ...state.dirtyProperties,
                    [versionId]: omit(state.dirtyProperties[versionId], versionPropertyId)
                }
            };
        }
    ),

    on(TranslationPageActions.cancelTranslations, (state): TranslationPageState => {
        return { ...state, dirtyProperties: {} };
    }),

    on(TranslationPageActions.saveDirtyVersionsSuccess, (state): TranslationPageState => {
        return {
            ...state,
            loaded: true,
            dirtyProperties: {}
        };
    }),
    on(
        TranslationPageActions.showAllElements,
        (state): TranslationPageState => ({ ...state, isShowingAllElements: true })
    ),
    on(
        TranslationPageActions.hideAllElements,
        (state): TranslationPageState => ({ ...state, isShowingAllElements: false })
    ),

    on(
        TranslationPageActions.initBfFeeds,
        (state, { feeds }): TranslationPageState => ({ ...state, feedSources: feeds })
    )
);
